import * as React from 'react';
import { Avatar, Box, Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, useTheme } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { Link, useNavigate } from 'react-router-dom';
import { HeaderAppBar, HeaderToolbar } from '../DashboardLayout/style';
import logo360 from '../../assets/logo/PRIME360WhiteLogo.svg';
import { getInitialsOfName } from '../../utils';
import { useAuth } from '../../hooks/useAuth';

// Header for the confined /update-customer-orders page. Same look as the dashboard
// header (HeaderAppBar + logo), but with NO nav toggle and NO notifications, and a
// user dropdown that offers only Logout. The dashboard's Header/AccountMenu are left
// untouched.
function OrderUpdateHeader() {
    const theme = useTheme();
    const { getUserDetailsNotAsync } = useAuth();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const userData = getUserDetailsNotAsync() || {};
    const contact = userData.contact || {};
    const name = contact.name || '';
    // name/email come from Salesforce via the order-update getInitialDetails branch.
    // Until that payload is populated (Apex deploy), fall back to the verified email
    // from the Cognito token so at least the email shows.
    const email = contact.email || userData?.tokens?.idToken?.payload?.['email'] || '';
    const initials = getInitialsOfName(name) || (email ? email.charAt(0).toUpperCase() : '');

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleLogout = (e) => {
        e.preventDefault();
        navigate('/signout' + window.location.search, { replace: true });
    };

    return (
        <HeaderAppBar position="static" elevation={0}>
            <HeaderToolbar>
                <Box display="flex" alignItems="center">
                    <img src={logo360} height={32} width={105} alt="Logo" />
                </Box>

                <Box>
                    <Tooltip title="My Account">
                        <Button
                            onClick={handleClick}
                            aria-controls={open ? 'order-update-account-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            endIcon={<KeyboardArrowDown />}
                            color="inherit"
                            sx={(theme) => ({
                                borderRadius: theme.typography.borderRadius.xxxLarge,
                                padding: '0.5rem',
                                textTransform: 'unset',
                                '&:hover': {
                                    backgroundColor: theme.palette.black[100],
                                },
                            })}
                        >
                            <Avatar sx={{ width: '2rem', height: '2rem' }}>{initials}</Avatar>
                        </Button>
                    </Tooltip>
                    <Menu
                        anchorEl={anchorEl}
                        id="order-update-account-menu"
                        open={open}
                        onClose={handleClose}
                        onClick={handleClose}
                        slotProps={{
                            paper: {
                                elevation: 0,
                                sx: {
                                    width: 288,
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                    mt: 1.5,
                                    '& .MuiAvatar-root': { width: 32, height: 32, ml: -0.5, mr: 1 },
                                    '&::before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: 'background.paper',
                                        transform: 'translateY(-50%) rotate(45deg)',
                                        zIndex: 0,
                                    },
                                    '& .MuiButtonBase-root.MuiMenuItem-root': {
                                        color: '#344054',
                                        fontWeight: theme.typography.fontWeightRegular,
                                        fontSize: theme.typography.customFontSize.small,
                                        padding: '10px 16px',
                                    },
                                    '& .MuiButtonBase-root.MuiMenuItem-root .MuiListItemIcon-root': {
                                        minWidth: 'unset',
                                        marginRight: '0.8rem',
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: '1rem', color: '#344054' },
                                },
                            },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <MenuItem disabled sx={{ '&.Mui-disabled': { opacity: 1 } }}>
                            <Avatar>{initials}</Avatar>
                            <ListItemText
                                primary={name}
                                secondary={email}
                                secondaryTypographyProps={{
                                    fontSize: 'customFontSize.small',
                                    fontWeight: 'fontWeightRegular',
                                    lineHeight: '1.25rem',
                                    color: 'grey.0',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                }}
                                primaryTypographyProps={{
                                    fontSize: 'customFontSize.small',
                                    fontWeight: 'fontWeightMedium',
                                    lineHeight: '1.25rem',
                                    color: 'grey.1100',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                }}
                            />
                        </MenuItem>
                        <Divider />
                        <MenuItem component={Link} to={'/signout'} onClick={handleLogout}>
                            <ListItemIcon>
                                <PowerSettingsNewIcon fontSize="small" />
                            </ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </Box>
            </HeaderToolbar>
        </HeaderAppBar>
    );
}

export default OrderUpdateHeader;
